// Serves local mock data for the read endpoints of the API, so the UI renders
// without the server. Unknown requests still go to the network.
// Remote image URLs inside the mock data are swapped for the local placeholder.
// Off unless REACT_APP_USE_MOCK_API=true: when on, it answers real backend
// routes (including POST /nftbyid) from these files instead of the API.
import axios from "axios";
import placeholderImage from "../Images/placeholder.svg";
import rank from "./rank.json";
import allnewnfts from "./allnewnfts.json";
import allcats from "./allcats.json";
import myallcollections from "./myallcollections.json";
import user from "./user.json";

const localize = (value) => {
  if (typeof value === "string") {
    return /^https?:\/\//.test(value) && /ipfs|nftstorage|seadn|\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(value)
      ? placeholderImage
      : value;
  }
  if (Array.isArray(value)) return value.map(localize);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, localize(v)])
    );
  }
  return value;
};

const collections = [...rank, ...myallcollections].filter(
  (c, i, all) => all.findIndex((x) => x._id === c._id) === i
);

const byName = (list, name) =>
  name
    ? list.filter((c) => (c.name || "").toLowerCase().includes(String(name).toLowerCase()))
    : list;

const routes = [
  ["get", /\/rank$/, () => rank],
  ["get", /\/allnewnfts$/, () => allnewnfts],
  ["get", /\/allcats$/, () => allcats],
  ["get", /\/myallcollections$/, () => myallcollections],
  ["get", /\/(airdropcollection|getairdropslider|getairdrop)$/, () => []],
  ["get", /\/allcollections\/search\/$/, (_, q) => byName(collections, q.get("name"))],
  // the real API returns a single object here, not a list
  ["get", /\/collectionbyid\/([^/]+)$/, (m) =>
    collections.find((c) => c._id === m[1] || String(c.Collection_id) === m[1]) || {}],
  ["get", /\/nftsinglebyid\/([^/]+)$/, (m) =>
    allnewnfts.find((n) => n._id === m[1]) || {}],
  ["post", /\/collectionbyname$/, (_, __, body) => byName(collections, body && body.name)],
  ["post", /\/collectionbycat$/, () => collections],
  ["post", /\/collectionbycreateor$/, () => myallcollections],
  ["post", /\/getuser$/, () => user],
  ["post", /\/nftbyid\/([^/]+)$/, (m) => allnewnfts.filter((n) => n._id === m[1])],
];

export const MOCK_API_ENABLED = process.env.REACT_APP_USE_MOCK_API === "true";

if (MOCK_API_ENABLED) {
  axios.interceptors.request.use((config) => {
    const base = window.apiURL;
    const url = config.url || "";
    if (!base || !url.startsWith(base)) return config;
    const [path, query = ""] = url.slice(base.length).split("?");
    const method = (config.method || "get").toLowerCase();
    for (const [m, re, handler] of routes) {
      const match = m === method && path.match(re);
      if (!match) continue;
      let body;
      try {
        body = typeof config.data === "string" ? JSON.parse(config.data) : config.data;
      } catch (e) {
        body = undefined;
      }
      const data = localize(handler(match, new URLSearchParams(query), body));
      config.adapter = () =>
        Promise.resolve({ data, status: 200, statusText: "OK", headers: {}, config, request: {} });
      break;
    }
    return config;
  });
}
